/**
 * Liga o stream SSE e traduz eventos realtime em reações no cache do
 * react-query (DESIGN-V8 Etapa 2). Chamado UMA vez, no AppLayout — só rotas
 * autenticadas do staff; widget público e portal ficam de fora.
 *
 * Payloads carregam só ids de propósito: a reação é sempre invalidar a query
 * certa e deixar o fetch normal (incremental no caso das mensagens) buscar o
 * conteúdo. `invalidateQueries` só refaz queries ATIVAS (montadas) — as
 * desmontadas ficam stale e refazem no próximo mount, que é o que queremos.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { startRealtime, subscribeRealtime } from '@/lib/realtime';
import {
  chatDetailKey,
  chatListsKey,
  chatMessagesKey,
  resyncChatMessages,
} from '@/features/chats/chats-queries';
import {
  ticketCommentsQuery,
  ticketDetailQuery,
  ticketEventsQuery,
} from '@/features/tickets/tickets-queries';
import { recentEventsKey } from '@/features/dashboard/dashboard-queries';
import { NOTIFICATIONS_KEY } from '@/features/notifications/notifications-queries';
import { PRESENCE_KEY } from '@/features/presence/presence-queries';
import { WORK_KEY } from '@/features/work/work-queries';
import { TIMELINE_KEY } from '@/features/atendimentos/timeline';

export function useRealtimeSync(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const stopStream = startRealtime();
    const unsubscribe = subscribeRealtime((event) => {
      switch (event.type) {
        case 'chat.message.created':
          // O fetch incremental (?after_id) apanha a mensagem nova. A lista e
          // o badge NÃO são invalidados aqui: toda mensagem que muda a lista
          // (preview/unread/updatedAt) vem seguida de um chat.updated, que já
          // invalida a família da lista — invalidar aqui também dobraria os
          // refetches. Nota interna não emite chat.updated, e de fato não
          // altera a lista.
          void qc.invalidateQueries({ queryKey: chatMessagesKey(event.chatId) });
          // spec 018: a mensagem aparece na linha do tempo do atendimento
          // vinculado (o evento não diz qual — só as montadas refazem) e muda
          // o "precisa de resposta" do Hoje
          void qc.invalidateQueries({ queryKey: TIMELINE_KEY });
          if (event.direction === 'inbound') void qc.invalidateQueries({ queryKey: WORK_KEY });
          break;
        case 'chat.message.updated':
          // Tick de entrega / backfill de mídia: campos de mensagem já
          // cacheada mudaram — o caminho incremental não veria. Resync
          // completo das mensagens + a linha do tempo (substitui o resync
          // periódico de ~12s); o "tentar de novo" da mídia faz o mesmo.
          void resyncChatMessages(qc, event.chatId);
          break;
        case 'chat.updated':
          // Estado/responsável/mesa/leitura: detalhe + lista + badge.
          void qc.invalidateQueries({ queryKey: chatDetailKey(event.chatId) });
          void qc.invalidateQueries({ queryKey: chatListsKey });
          void qc.invalidateQueries({ queryKey: WORK_KEY });
          break;
        case 'ticket.comment.created':
          // Dois atendentes no mesmo ticket passam a se ver sem F5.
          void qc.invalidateQueries({ queryKey: ticketCommentsQuery(event.ticketNumber).queryKey });
          void qc.invalidateQueries({ queryKey: [...TIMELINE_KEY, { ticketNumber: event.ticketNumber }] });
          void qc.invalidateQueries({ queryKey: WORK_KEY });
          break;
        case 'ticket.event.created':
          // Evento notificável (v12): o SINO re-busca o feed na hora — é ele
          // que dispara badge, som e notificação do navegador (a decisão de
          // alertar mora no sino, que conhece prefs e ator; aqui só sincroniza
          // o cache). O ticket aberto também acompanha status/estágio/
          // responsável sem F5.
          void qc.invalidateQueries({ queryKey: recentEventsKey });
          // spec 037: o sino, a página /notificacoes e as Últimas respostas
          // (só as montadas refazem)
          void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
          void qc.invalidateQueries({ queryKey: ticketDetailQuery(event.ticketNumber).queryKey });
          void qc.invalidateQueries({ queryKey: ticketEventsQuery(event.ticketNumber).queryKey });
          void qc.invalidateQueries({ queryKey: [...TIMELINE_KEY, { ticketNumber: event.ticketNumber }] });
          void qc.invalidateQueries({ queryKey: WORK_KEY });
          break;
        case 'presence.changed':
          // spec 015: alguém entrou/saiu de pausa — o chip (se foi em outra aba
          // da mesma pessoa), a lista de transferência e a página de Pausas
          // refazem sem F5
          void qc.invalidateQueries({ queryKey: PRESENCE_KEY });
          break;
      }
    });
    return () => {
      unsubscribe();
      stopStream();
    };
  }, [qc]);
}
